"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Word = void 0;
const talqis_1 = require("talqis");
const uuid_1 = require("uuid");
const error_1 = require("./error");
const parser_1 = require("./parser");
class Word {
    constructor(uniqueName, date, contents) {
        this.uid = (0, uuid_1.v1)();
        this.uniqueName = uniqueName;
        this.date = date;
        this.contents = contents;
        this.update(false);
    }
    static createEmpty() {
        const name = "";
        const date = talqis_1.NewHairianDate.current().getHairia(true);
        const contents = { ja: "+ <>\n= <>\n\nM:" };
        const word = new Word(name, date, contents);
        return word;
    }
    static fromPlain(plain) {
        const uniqueName = plain.uniqueName;
        const date = plain.date;
        const contents = plain.contents;
        const word = new Word(uniqueName, date, contents);
        word.writable.uid = plain.uid;
        return word;
    }
    toPlain() {
        const uid = this.uid;
        const uniqueName = this.uniqueName;
        const date = this.date;
        const contents = this.contents;
        return { uid, uniqueName, date, contents };
    }
    setDictionary(dictionary) {
        this.writable.dictionary = dictionary;
        this.updateComparisonString();
    }
    reissueUid() {
        this.writable.uid = (0, uuid_1.v1)();
    }
    refreshDate() {
        const date = talqis_1.NewHairianDate.current().getHairia(true);
        this.writable.date = date;
    }
    copy() {
        this.ensureDictionary();
        const word = new Word(this.uniqueName, this.date, this.contents);
        word.setDictionary(this.dictionary);
        return word;
    }
    edit(newWord, skipValidate) {
        const errorType = (skipValidate) ? null : this.validateEdit(newWord);
        if (errorType === null) {
            this.writable.uniqueName = newWord.uniqueName;
            this.writable.date = newWord.date;
            this.writable.contents = Object.fromEntries(Object.entries(newWord.contents).map(([language, content]) => [language, content === null || content === void 0 ? void 0 : content.trim()]));
            this.update(true);
        }
        else {
            throw new error_1.ValidationError(errorType);
        }
    }
    validateEdit(newWord) {
        if (!Word.isValidUniqueName(newWord.uniqueName)) {
            return "invalidUniqueName";
        }
        else {
            return null;
        }
    }
    get markers() {
        this.ensureDictionary();
        const markers = this.dictionary.markers.get(this.uniqueName);
        return markers;
    }
    toggleMarker(marker) {
        this.ensureDictionary();
        this.dictionary.markers.toggle(this.uniqueName, marker);
    }
    update(full) {
        this.updateName();
        this.updateEquivalentNames();
        if (full) {
            this.updateComparisonString();
        }
    }
    updateName() {
        const name = this.uniqueName.replace(/~/g, "");
        this.writable.name = name;
    }
    updateEquivalentNames() {
        var _a, _b;
        const equivalentNames = {};
        const parser = parser_1.Parser.createSimple();
        for (const [language] of Object.entries(this.contents)) {
            equivalentNames[language] = [...((_a = parser.lookupEquivalentNames(this, language)) !== null && _a !== void 0 ? _a : []), ...((_b = parser.lookupPhraseEquivalentNames(this, language)) !== null && _b !== void 0 ? _b : [])];
        }
        this.writable.equivalentNames = equivalentNames;
    }
    updateComparisonString() {
        this.ensureDictionary();
        let comparisonString = "";
        const alphabetRule = this.dictionary.settings.alphabetRule;
        const apostrophe = alphabetRule.includes("'");
        for (let i = 0; i < this.uniqueName.length; i++) {
            const char = this.uniqueName.charAt(i);
            if ((apostrophe || char !== "'") && char !== "-" && char !== "+" && char !== "~") {
                const position = alphabetRule.indexOf(char);
                if (position >= 0) {
                    comparisonString += String.fromCodePoint(position + 200);
                }
                else {
                    comparisonString += String.fromCodePoint(1000);
                }
            }
        }
        const match = this.uniqueName.match(/^(\+)?(')?(.+?)(')?(\+)?(~*)$/);
        if (match) {
            if (match[2]) {
                comparisonString += String.fromCodePoint(150);
            }
            if (match[4]) {
                comparisonString += String.fromCodePoint(151);
            }
            if (match[1]) {
                comparisonString += String.fromCodePoint(160);
            }
            if (match[5]) {
                comparisonString += String.fromCodePoint(161);
            }
            if (match[6].length > 0) {
                comparisonString += String.fromCodePoint(match[6].length + 100);
            }
        }
        else {
            throw new Error("cannot happen");
        }
        this.comparisonString = comparisonString;
    }
    ensureDictionary() {
        if (!this.dictionary) {
            throw new Error("no dictionary set");
        }
    }
    static sortWords(words) {
        const sortedWords = words.sort((firstWord, secondWord) => {
            const firstComparisonString = firstWord.comparisonString;
            const secondComparisonString = secondWord.comparisonString;
            if (firstComparisonString < secondComparisonString) {
                return -1;
            }
            else if (firstComparisonString > secondComparisonString) {
                return 1;
            }
            else {
                return 0;
            }
        });
        return sortedWords;
    }
    static isValidUniqueName(uniqueName) {
        return uniqueName.match(/^(\+)?((?:\p{L}|-|')+?)(\+)?(~*)$/u) !== null;
    }
    get writable() {
        return this;
    }
}
exports.Word = Word;
